import { GenderTypeLabels } from '@/constants/GenderTypeLabels';
import GenderType from '@/enums/genderType';
import ToiletTypes from '@/enums/toiletTypes';
import HadrianBaseWithImage from '@/models/HadrianBaseWithImage';
import Room from '@/models/room/room';
import iconInfoBubble from '@/shared/icons/iconInfoBubble/iconInfoBubble.vue';
import hadrianLoader from '@/shared/viewParts/hadrianLoader/hadrianLoader.vue';
import { defineComponent } from 'vue';
import hadrianArbitraryTooltip from '../hadrianArbitraryTooltip/hadrianArbitraryTooltip.vue';
import hadrianCheckbox from '../hadrianCheckbox/hadrianCheckbox.vue';
import hadrianToiletType from '../hadrianToiletType/hadrianToiletType.vue';
import hadrianTooltip from '../hadrianTooltip/hadrianTooltip.vue';

interface IProjectSettingsItem extends HadrianBaseWithImage {
  label: string;
}

interface IProjectInfo {
  label: string;
  description?: string;
}

export default defineComponent({
  name: 'minimalRoomList',
  components: {
    hadrianTooltip,
    hadrianCheckbox,
    hadrianArbitraryTooltip,
    hadrianToiletType,
    iconInfoBubble,
    hadrianLoader,
  },
  props: {
    rooms: {
      type: Array as () => Room[],
      required: true,
    },
    isSelected: {
      type: Function as never as () => (roomId: string) => boolean,
      required: true,
    },
    toggleSelection: {
      type: Function as never as () => () => void,
      required: false,
    },
    onSelect: {
      type: Function as never as () => (room: Room) => void,
      required: true,
    },
    isSelectable: {
      type: Function as never as () => (room: Room) => boolean,
      default: () => true,
    },
    perPage: {
      type: Number,
      default: 6,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    allSelected: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
    };
  },
  computed: {
    shownRooms(): Room[] {
      const startIndex = (this.currentPage - 1) * this.perPage;
      return this.rooms.slice(startIndex, startIndex + this.perPage);
    },
    hasRooms(): boolean {
      return this.rooms.length > 0;
    },
    numSelectableRooms(): number {
      return this.rooms.length;
    },
    numSelectedRooms(): number {
      return this.rooms.filter(room => this.isSelected(room.id)).length;
    },
  },
  methods: {
    selectRoom(room: Room): void {
      if (!this.isSelectable(room)) {
        return;
      }
      this.onSelect(room);
    },
    projectSettingsInfo(room: Room): IProjectInfo[] {
      const t = (key: string) => this.$t(key).toString();
      const stringifyArray = (arr: Array<{ label: string }>) =>
        arr.map(item => item.label).join(', ') || t('rooms.none');

      if (room.roomSettings) {
        const { doorHinge, latch, noSightLines, options } = room.roomSettings.hardwarePackage;
        const items: IProjectInfo[] = [
          { label: t('rooms.doorHinges'), description: doorHinge.description },
          { label: t('rooms.latch'), description: latch?.description },
          { label: t('rooms.noSightLines'), description: stringifyArray(noSightLines) },
          { label: t('rooms.options'), description: stringifyArray(options) },
        ];

        return items.filter(item => Boolean(item.description));
      } else {
        return [];
      }
    },
    projectSettingsPictures(room: Room): IProjectSettingsItem[] {
      const roomSettings = room.roomSettings;
      if (roomSettings) {
        return [
          { ...roomSettings.partition, image: roomSettings.partition.icon },
          { ...roomSettings.material, image: roomSettings.material.icon },
          roomSettings.hardwarePackage.hardwareMaterial,
          roomSettings.finish,
          roomSettings.series,
        ];
      }
      return [];
    },
    getRoomFloor(room: Room): string {
      return (room.floor ?? '-').toString();
    },
    getGenderIcon(room: Room): any {
      switch (room.gender) {
        case GenderType.Woman:
          return ToiletTypes.Women;
        case GenderType.Man:
          return ToiletTypes.Men;
        case GenderType.Mixed:
        default:
          return ToiletTypes.Mixed;
      }
    },
    roomTypeLabel(room: Room): string {
      return GenderTypeLabels[room.gender];
    },
  },
});
